import { forwardRef, JSX, useEffect, useMemo, useState } from 'react'

import { cn } from '@/utils'
import { cva } from 'class-variance-authority'

interface ButtonProps {
  variant?: 'outline' | 'ghost'
  size?: 'default' | 'icon'
}

interface PaginationInterface {
  total: number
  itemsPerPage: number
  currentPage: number
  onPageChange: (page: number) => void
}

// ICONS
const ChevronLeft = ({ className }: React.SVGProps<SVGSVGElement>) => (
  <>
    <svg
      className={className}
      fill="none"
      height="24"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width="24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="m15 18-6-6 6-6" />
    </svg>
  </>
)
const ChevronRight = ({ className }: React.SVGProps<SVGSVGElement>) => (
  <>
    <svg
      className={className}
      fill="none"
      height="24"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width="24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="m9 18 6-6-6-6" />
    </svg>
  </>
)
const HorizontalMore = ({ className }: React.SVGProps<SVGSVGElement>) => (
  <>
    <svg
      className={className}
      fill="none"
      height="24"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width="24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="12" cy="12" r="1"></circle>
      <circle cx="19" cy="12" r="1"></circle>
      <circle cx="5" cy="12" r="1"></circle>
    </svg>
  </>
)
// ICONS

const buttonVariants = cva(
  'inline-flex cursor-pointer items-center justify-center rounded-md text-sm font-medium whitespace-nowrap ring-offset-transparent transition-colors focus-visible:ring-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        outline: 'border border-gray-500 bg-transparent hover:bg-gray-300',
        ghost: 'hover:bg-gray-300'
      },
      size: {
        default: 'h-10 px-4 py-2',
        icon: 'size-10'
      }
    },
    defaultVariants: {
      variant: 'ghost',
      size: 'icon'
    }
  }
)

const PaginationWrap = ({ className, ...props }: React.ComponentProps<'nav'>) => (
  <nav
    aria-label="pagination"
    className={cn('mx-auto flex w-full justify-center', className)}
    role="navigation"
    {...props}
  />
)
PaginationWrap.displayName = 'PaginationWrap'

const PaginationContent = forwardRef<HTMLUListElement, React.ComponentProps<'ul'>>(
  ({ className, ...props }, ref) => (
    <ul
      className={cn('flex flex-row items-center gap-1', className)}
      ref={ref}
      {...props}
    />
  )
)
PaginationContent.displayName = 'PaginationContent'

const PaginationItem = forwardRef<HTMLLIElement, React.ComponentProps<'li'>>(
  ({ className, ...props }, ref) => (
    <li className={cn('', className)} ref={ref} {...props} />
  )
)
PaginationItem.displayName = 'PaginationItem'

type PaginationLinkProps = {
  isActive?: boolean
} & ButtonProps &
  React.ComponentProps<'a'>

const PaginationLink = ({
  className,
  isActive,
  size = 'icon',
  ...props
}: PaginationLinkProps) => (
  <a
    aria-current={isActive ? 'page' : undefined}
    className={cn(
      buttonVariants({
        variant: 'ghost',
        size
      }),
      'border hover:border-border hover:bg-transparent',
      isActive ? 'border-border' : 'text-muted-foreground border-transparent',
      className
    )}
    {...props}
  />
)
PaginationLink.displayName = 'PaginationLink'

const PaginationPrevious = ({
  className,
  ...props
}: React.ComponentProps<typeof PaginationLink>) => (
  <PaginationLink
    aria-label="Go to previous page"
    className={cn('gap-1 pl-2.5', className)}
    size="default"
    {...props}
  >
    <ChevronLeft className="size-4" />
    <span>Poprzednia</span>
  </PaginationLink>
)
PaginationPrevious.displayName = 'PaginationPrevious'

const PaginationNext = ({
  className,
  ...props
}: React.ComponentProps<typeof PaginationLink>) => (
  <PaginationLink
    aria-label="Go to next page"
    className={cn('gap-1 pr-2.5', className)}
    size="default"
    {...props}
  >
    <span>Następna</span>
    <ChevronRight className="size-4" />
  </PaginationLink>
)
PaginationNext.displayName = 'PaginationNext'

const PaginationEllipsis = ({ className, ...props }: React.ComponentProps<'span'>) => (
  <span
    aria-hidden
    className={cn('flex h-9 w-9 items-center justify-center', className)}
    {...props}
  >
    <HorizontalMore className="size-4" />
    <span className="sr-only">Więcej...</span>
  </span>
)
PaginationEllipsis.displayName = 'PaginationEllipsis'

const FirstPage = ({ pages }: { pages: JSX.Element[] }) => {
  const lastPage = pages.slice(-1)
  const firstPages = pages.slice(0, 3)
  return (
    <>
      {firstPages}
      <PaginationItem>
        <PaginationEllipsis />
      </PaginationItem>
      {lastPage}
    </>
  )
}

const PageInMiddle = ({
  pages,
  currentPage,
  totalPages
}: {
  pages: JSX.Element[]
  currentPage: number
  totalPages: number
}) => {
  const firstPage = pages[0]
  const lastPage = pages.slice(-1)
  const firstPages = [pages[currentPage - 2], pages[currentPage - 1], pages[currentPage]]
  return (
    <>
      {currentPage !== 2 && firstPage}
      {currentPage !== 2 && (
        <PaginationItem>
          <PaginationEllipsis />
        </PaginationItem>
      )}
      {firstPages}
      {currentPage !== totalPages - 1 && (
        <PaginationItem>
          <PaginationEllipsis />
        </PaginationItem>
      )}
      {currentPage !== totalPages - 1 && lastPage}
    </>
  )
}

const LastPage = ({ pages }: { pages: JSX.Element[] }) => {
  const firstPage = pages[0]
  const firstPages = pages.slice(-3)
  return (
    <>
      {firstPage}
      <PaginationItem>
        <PaginationEllipsis />
      </PaginationItem>
      {firstPages}
    </>
  )
}

export const Pagination = ({
  total,
  itemsPerPage,
  currentPage,
  onPageChange
}: PaginationInterface) => {
  const [totalPages, setTotalPages] = useState(0)

  useEffect(() => {
    if (total > 0 && itemsPerPage > 0) setTotalPages(Math.ceil(total / itemsPerPage))
  }, [total, itemsPerPage])

  const paginationItems = useMemo(() => {
    const pages = []
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <PaginationItem key={'pagination_' + i} onClick={() => onPageChange(i)}>
          <PaginationLink isActive={i === currentPage}>{i}</PaginationLink>
        </PaginationItem>
      )
    }
    if (pages.length === 1) return null
    if (pages.length < 4) return pages
    else {
      if (currentPage === 1) return <FirstPage pages={pages} />
      else if (currentPage === pages.length) return <LastPage pages={pages} />
      else
        return (
          <PageInMiddle currentPage={currentPage} pages={pages} totalPages={totalPages} />
        )
    }
  }, [totalPages, currentPage])
  // if (total === 0)
  //   return (
  //     <div className="w-full text-center text-lg">Nie znaleziono żadnych wyników.</div>
  //   )

  if (!total) return null

  return (
    <PaginationWrap>
      <PaginationContent>
        {currentPage !== 1 && (
          <PaginationItem onClick={() => onPageChange(currentPage - 1)}>
            <PaginationPrevious />
          </PaginationItem>
        )}
        {paginationItems}
        {currentPage !== totalPages && (
          <PaginationItem onClick={() => onPageChange(currentPage + 1)}>
            <PaginationNext />
          </PaginationItem>
        )}
      </PaginationContent>
    </PaginationWrap>
  )
}
